import React, {useEffect, useState} from "react";
import {HelmetProvider} from "react-helmet-async";
import {Helmet} from "react-helmet";
import {Person} from "../../models/Person";
import AdminLayout from "../components/AdminLayout";
import {BASE_URLS} from "../../config/urls";
import {apiFetch} from "../../utils/api";
import "../styles/admin-discount.css";
import {BtnSpinner} from "../../components/fields/BtnSpinner";

/** ======= Modal shell (как ты дал) ======= */
export function ModalShell({
                               title,
                               subtitle,
                               children,
                               onClose,
                           }: {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    onClose: () => void;
}) {
    return (
        <div className="profile-modal-overlay" onMouseDown={onClose}>
            <div className="profile-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="profile-modal-head">
                    <div>
                        <div className="profile-modal-title">{title}</div>
                        {subtitle && <div className="profile-modal-subtitle">{subtitle}</div>}
                    </div>
                    <button className="profile-modal-close" onClick={onClose} aria-label="Закрыть">
                        ✕
                    </button>
                </div>
                <div className="profile-modal-body">{children}</div>
            </div>
        </div>
    );
}

/** ======= Встроенная модалка изменения скидки ======= */
function DiscountEditModal({
                               person,
                               onClose,
                               onSaved,
                           }: {
    person: Person;
    onClose: () => void;
    onSaved: (updated: Person) => void;
}) {
    const [discount, setDiscount] = useState<string>(String(person.discount ?? ""));
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string>("");

    // ESC закрывает модалку
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    const normalize = (value: string) => {
        // оставляем только цифры, максимум 3 символа (0..100)
        const digits = value.replace(/\D/g, "").slice(0, 3);
        if (digits === "") return "";
        const n = Math.min(100, Math.max(0, Number(digits)));
        return String(n);
    };

    const onChangeDiscount = (e: React.ChangeEvent<HTMLInputElement>) => {
        setError("");
        setDiscount(normalize(e.target.value));
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSaving) return;

        const n = discount === "" ? 0 : Number(discount);
        if (Number.isNaN(n) || n < 0 || n > 100) {
            setError("Скидка должна быть числом от 0 до 100.");
            return;
        }

        try {
            setIsSaving(true);
            setError("");

            const updated: Person = {...person, discount: n as any};

            const res = await apiFetch(`${BASE_URLS.api}/admin_change_discount`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(updated),
            });

            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || String(res.status));
            }

            onSaved(updated);
            onClose();
        } catch (err: any) {
            console.error(err);
            setError(err?.message || "Не удалось изменить скидку");
            setIsSaving(false);
        }
    };

    return (
        <ModalShell
            title="Изменить скидку"
            subtitle={`${person.name ?? "Пользователь"} • ${person.email ?? "—"}`}
            onClose={onClose}
        >
            <form onSubmit={submit} className="discount-modal-form">
                <label className="admin_title" htmlFor="discountValue">
                    Скидка (%)
                </label>

                <div className="discount-modal-row">
                    <input
                        id="discountValue"
                        className="discount-admin-input"
                        inputMode="numeric"
                        placeholder={String(person.discount ?? 0)}
                        value={discount}
                        onChange={onChangeDiscount}
                        autoFocus
                    />
                    <button type="submit" className="discount-admin-button" disabled={isSaving}>
                        {isSaving ? <BtnSpinner/> : "Сохранить"}
                    </button>
                </div>

                {error && <div className="discount-modal-error">{error}</div>}

                <div className="discount-modal-hint">
                    Допустимое значение: 0–100
                </div>
            </form>
        </ModalShell>
    );
}

/** ======= Страница ======= */
const DiscountAdminPage: React.FC = () => {
    const [email, setEmail] = useState("");
    const [people, setPeople] = useState<Array<Person>>([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [personToChange, setPersonToChange] = useState<Person | null>(null);

    const openModal = (person: Person) => {
        setPersonToChange(person);
        setIsModalOpen(true);
        document.body.style.overflow = "hidden";
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setPersonToChange(null);
        document.body.style.overflow = "auto";
    };

    async function handleForm(event: React.FormEvent) {
        event.preventDefault();

        try {
            const resp = await apiFetch(`${BASE_URLS.api}/admin_discount`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: email.trim(),
            });

            if (!resp.ok) {
                const txt = await resp.text();
                throw new Error(txt || String(resp.status));
            }

            const data = await resp.json();
            setPeople(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setPeople([]);
        }
    }

    function handleEmail(e: React.ChangeEvent<HTMLInputElement>) {
        setEmail(e.target.value);
    }

    const applyUpdatedPerson = (updated: Person) => {
        // обновим в таблице по email (или по id если у тебя он есть)
        setPeople((prev) =>
            prev.map((p) => (p.email && updated.email && p.email === updated.email ? {...p, ...updated} : p))
        );
    };

    return (
        <AdminLayout isPopupVisible={isModalOpen}>
            <HelmetProvider>
                <Helmet title="Админка скидок"/>
            </HelmetProvider>

            <div className="discount-admin-page">
                {/* ===== Модалка прямо тут ===== */}
                {isModalOpen && personToChange && (
                    <DiscountEditModal
                        person={personToChange}
                        onClose={closeModal}
                        onSaved={applyUpdatedPerson}
                    />
                )}

                <section className="discount-admin-card" style={{marginTop: 90}}>
                    <h1 className="discount-admin-heading">Управление скидками</h1>
                    <p className="discount-admin-subtitle">
                        Найдите пользователя по email и измените размер его скидки.
                    </p>

                    <form className="discount-admin-form" onSubmit={handleForm}>
                        <label className="admin_title" htmlFor="discount-email">
                            Email пользователя
                        </label>
                        <div className="discount-admin-form-row">
                            <input
                                id="discount-email"
                                type="text"
                                value={email}
                                onChange={handleEmail}
                                className="discount-admin-input"
                                placeholder="example@mail.ru"
                            />
                            <button type="submit" className="discount-admin-button">
                                Найти
                            </button>
                        </div>
                    </form>
                </section>

                <section className="discount-admin-card">
                    <div className="discount-admin-table-header">
                        <h2 className="discount-admin-heading-small">Найденные пользователи</h2>
                        <span className="discount-admin-count">
              {people.length > 0 ? `${people.length} результат(ов)` : "Нет результатов"}
            </span>
                    </div>

                    <div className="discount-admin-table-wrapper">
                        <table className="admin_table discount-admin-table">
                            <thead>
                            <tr>
                                <th style={{width: "25%"}}>Имя</th>
                                <th style={{width: "35%"}}>Email</th>
                                <th style={{width: "25%"}}>Скидка</th>
                                <th style={{width: "15%"}}>Действие</th>
                            </tr>
                            </thead>
                            <tbody>
                            {people.map((person, index) => (
                                <tr key={person.email ?? index}>
                                    <td>{person.name}</td>
                                    <td>{person.email}</td>
                                    <td>
                                        <span className="discount-admin-badge">{(person.discount as any) ?? 0}%</span>
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="discount-admin-button-secondary"
                                            onClick={() => openModal(person)}
                                        >
                                            Изменить
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {people.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="discount-admin-empty">
                                        Для отображения результатов укажите email и нажмите «Найти».
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </AdminLayout>
    );
};

export default DiscountAdminPage;
